import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Download, CalendarDays, MessageCircle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useContactChannel } from '@/hooks/useContactChannel';
import { trackFunnelEvent } from '@/services/analytics';

// Lead Magnet funnel page — strictly noindex (never organically indexable).
export default function StudyAbroadThankYouPage() {
  const { flags } = useFeatureFlags();
  const channel = useContactChannel();
  const lastDownloadRef = useRef(0);

  // Debounced single-fire: rapid repeated clicks (within 2s) are ignored so
  // lead_magnet_downloaded fires exactly once per intentional download action.
  const handleGuideDownload = () => {
    const now = Date.now();
    if (now - lastDownloadRef.current < 2000) return;
    lastDownloadRef.current = now;
    trackFunnelEvent('lead_magnet_downloaded', {});
  };

  useEffect(() => {
    document.title = 'Thank You | ZAFCO Study Abroad';
    let meta = document.querySelector('meta[name="robots"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    const prev = meta.getAttribute('content');
    meta.setAttribute('content', 'noindex, nofollow');
    trackFunnelEvent('thank_you_page_viewed', {});
    return () => {
      if (created) meta.remove();
      else if (prev) meta.setAttribute('content', prev);
    };
  }, []);

  const basePdfPath = flags.pdf_asset_path || '/assets/guides/study-abroad-2026.pdf';
  // Cache-busting: version query param busts browser cache when a new edition
  // (2027/2028…) is deployed — just bump PDF_ASSET_VERSION, no code change.
  const pdfPath = flags.pdf_asset_version ? `${basePdfPath}?v=${flags.pdf_asset_version}` : basePdfPath;
  const calendlyUrl = flags.calendly_booking_url;
  const whatsappHref = channel?.whatsappUrl || 'https://wa.me/';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-16" data-testid="sa-thank-you-page">
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 sm:p-10 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-9 h-9 text-emerald-600" aria-hidden="true" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">You're Verified — Thank You!</h1>
        <p className="text-sm text-slate-500 mt-2 mb-8">
          A ZAFCO counsellor will reach out on your WhatsApp shortly with your profile evaluation.
          Meanwhile, grab your free guide below.
        </p>

        <a
          href={pdfPath}
          download
          onClick={handleGuideDownload}
          className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl bg-[#003087] hover:bg-[#00246a] text-white font-semibold text-sm transition-colors"
          data-testid="ty-download-guide-btn"
        >
          <Download className="w-4 h-4" aria-hidden="true" />
          Download 'Study Abroad Guide 2026' PDF
        </a>

        <Link
          to="/study-abroad/book-counselling"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl border-2 border-[#003087] text-[#003087] hover:bg-blue-50 font-semibold text-sm transition-colors"
          data-testid="ty-book-counselling-btn"
        >
          Book Free Counselling <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </Link>

        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium text-sm transition-colors"
          data-testid="ty-whatsapp-btn"
        >
          <MessageCircle className="w-4 h-4" aria-hidden="true" /> WhatsApp Direct Chat
        </a>

        {calendlyUrl && (
          <a
            href={calendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl text-slate-500 hover:text-slate-700 text-xs font-medium underline underline-offset-4 transition-colors"
            data-testid="ty-calendly-btn"
          >
            <CalendarDays className="w-4 h-4" aria-hidden="true" /> Prefer a scheduled call? Pick a slot
          </a>
        )}
      </div>
    </div>
  );
}
